import { put, takeEvery, takeLatest, call, race, delay, cancelled } from "redux-saga/effects";
import moment from "moment-timezone";
import { PICTREE, ENDPOINT } from "../../constants";
import {
  setPicTreeRegularSeasonEventPageData,
  setPicTreeRegularSeasonEventPageLoading,
  setPageTopLoader,
} from "actions";
import { getJwtToken, getDataFromSxi } from "api";

const EVENT_SELECTION_ORDER = {
  cmp4yz2lj00017rtcc4ok0jgv: 0,
  cmrz0wo8t0000bpzcana86bow: 1,
  cmunxinty000079zc6zf595vs: 2,
};

const EVENT_LABEL_BY_GAME_SPACE_ID = {
  cmp4yz2lj00017rtcc4ok0jgv: "レギュラーシーズン1",
  cmrz0wo8t0000bpzcana86bow: "レギュラーシーズン2",
  cmunxinty000079zc6zf595vs: "レギュラーシーズン3",
};

const COMPANY_NAME_BY_CODE = {
  NTT_ME: "NTT ME",
  TEPCO: "東京電力 TEPCO",
  NTT_FT: "NTT FT",
  HEPCO: "ほくでん",
};

const ALL_BOOST_EVENTS_VALUE = "__ALL__";

const SXI_HEAVY_RETRY = {
  timeoutMs: 60000,
  retries: 1,
  backoffBase: 1500,
};

function* callWithTimeoutAndRetry(fn, args = [], opts = {}) {
  const {
    timeoutMs = 20000,
    retries = 2,
    backoffBase = 1000,
    backoffFactor = 2,
    jitter = true,
    isRetryable = (err) => {
      const msg = (err && (err.message || err.errorMessage)) || "";
      return /timeout|timed out|504|gateway\s*time-?out/i.test(msg);
    },
  } = opts;

  let attempt = 0;
  while (true) {
    attempt += 1;

    try {
      const { response, timeout } = yield race({
        response: call(fn, ...args),
        timeout: delay(timeoutMs),
      });

      if (timeout) {
        throw new Error(`timeout after ${timeoutMs}ms`);
      }
      return response;
    } catch (err) {
      if (attempt > retries || !isRetryable(err)) {
        throw err;
      }

      const wait = Math.min(backoffBase * Math.pow(backoffFactor, attempt - 1), 10000);
      const jitterMs = jitter ? Math.floor(Math.random() * (wait * 0.3)) : 0;
      yield delay(wait + jitterMs);
    } finally {
      if (yield cancelled()) {
        // no-op
      }
    }
  }
}

const normalizeSelectionSeasonIds = (selectedSeasonIds, selectedSeasonId) => {
  if (Array.isArray(selectedSeasonIds) && selectedSeasonIds.length > 0) {
    return selectedSeasonIds.filter(Boolean);
  }

  return selectedSeasonId ? [selectedSeasonId] : [];
};

const sortSeasonIds = (seasonIds) => Array.from(seasonIds || [])
  .slice()
  .sort((left, right) => (EVENT_SELECTION_ORDER[left] ?? Number.MAX_SAFE_INTEGER) - (EVENT_SELECTION_ORDER[right] ?? Number.MAX_SAFE_INTEGER));

const resolveSeasonLabel = (seasonId, seasonName) => {
  const explicitName = String(seasonName || "").trim();
  if (explicitName) {
    return explicitName;
  }

  return EVENT_LABEL_BY_GAME_SPACE_ID[seasonId] || String(seasonId || "");
};

const formatNumber = (value) => Number(value || 0).toLocaleString("ja-JP");

const formatCurrency = (value) => `¥${formatNumber(Number(value || 0))}`;

const emptyTrend = () => ({
  date: [],
  uniqueShots: [],
  reviewerOk: [],
  uniqueShooters: [],
});

const emptySeasonChart = () => ({
  labels: [],
  series: [],
});

const emptyKpis = () => ([
  {
    key: "uniqueShot",
    title: "ユニーク撮影本数",
    value: "--",
    loading: true,
  },
  {
    key: "achievementRate",
    title: "撮影達成率",
    value: "--",
    subtitle: "対象 --本",
    loading: true,
  },
  {
    key: "reviewOk",
    title: "レビューOK数",
    value: "--",
    subtitle: "OK率 --%",
    loading: true,
  },
  {
    key: "uniqueShooter",
    title: "ユニーク撮影者数",
    value: "--",
    subtitle: "累計参加者",
    loading: true,
  },
  {
    key: "firstShooter",
    title: "初回撮影者数",
    value: "--",
    subtitle: "今期新規",
    loading: true,
  },
]);

const readyEmptyKpis = () => emptyKpis().map((item) => ({
  ...item,
  loading: false,
}));

const emptyPageData = (selectedSeasonId, selectedSeasonIds = selectedSeasonId ? [selectedSeasonId] : []) => ({
  selectedSeasonId,
  selectedSeasonIds,
  selectedBoostEventId: ALL_BOOST_EVENTS_VALUE,
  selectedDateRange: [null, null],
  kpis: emptyKpis(),
  trend: emptyTrend(),
  trendLoading: true,
  trendRows: [],
  trendRowsLoading: false,
  seasonChartLoading: false,
  seasonChart: emptySeasonChart(),
  bootstrapEventLoading: false,
  bootstrapEventReady: false,
  eventRowsLoading: true,
  eventRows: [],
});

const isAllBoostEvents = (boostEventId) => !boostEventId || boostEventId === ALL_BOOST_EVENTS_VALUE;

const buildBoostEventFilterClause = (boostEventId) => {
  if (isAllBoostEvents(boostEventId)) {
    return "";
  }

  return ` AND be.id = '${boostEventId}'`;
};

const buildEventDailyTrendQuery = (seasonIds, startUtc, endUtc, startJst, endJst, selectedBoostEventId) => {
  const seasonIdList = seasonIds.map((seasonId) => `'${seasonId}'`).join(", ");
  const boostEventFilterClause = buildBoostEventFilterClause(selectedBoostEventId);

  return {
    query: `
      WITH RECURSIVE date_series AS (
        SELECT DATE('${startJst}') AS day_jst
        UNION ALL
        SELECT DATE_ADD(day_jst, INTERVAL 1 DAY)
        FROM date_series
        WHERE day_jst < DATE('${endJst}')
      ),
      selected_events AS (
        SELECT
          be.id AS boost_event_id,
          be.game_space_id,
          be.start_datetime,
          be.end_datetime
        FROM boost_event be
        WHERE be.game_space_id IN (${seasonIdList})
          AND be.start_datetime < '${endUtc}'
          AND be.end_datetime >= '${startUtc}'
          ${boostEventFilterClause}
      ),
      approved_rows AS (
        SELECT DISTINCT
          DATE_FORMAT(CONVERT_TZ(rre.occurred_datetime, 'UTC', 'Asia/Tokyo'), '%Y-%m-%d') AS day_jst,
          rr.power_asset_id,
          rr.player_id
        FROM selected_events se
        JOIN review_request_event rre
          ON rre.game_space_id = se.game_space_id
         AND rre.type = 'APPROVED'
         AND rre.occurred_datetime >= '${startUtc}'
         AND rre.occurred_datetime < '${endUtc}'
        JOIN review_request_event_of_approved rreoa
          ON rreoa.id = rre.id
        JOIN review_request rr
          ON rr.id = rre.review_request_id
        JOIN boost_target_asset_list btal
          ON btal.boost_event_id = se.boost_event_id
         AND btal.power_asset_id = rr.power_asset_id
      ),
      daily_rows AS (
        SELECT
          day_jst,
          COUNT(DISTINCT power_asset_id) AS unique_shot_count,
          COUNT(DISTINCT power_asset_id) AS review_ok_count,
          COUNT(DISTINCT player_id) AS unique_shooter_count
        FROM approved_rows
        GROUP BY day_jst
      )
      SELECT
        ds.day_jst,
        COALESCE(dr.unique_shot_count, 0) AS unique_shot_count,
        COALESCE(dr.review_ok_count, 0) AS review_ok_count,
        COALESCE(dr.unique_shooter_count, 0) AS unique_shooter_count
      FROM date_series ds
      LEFT JOIN daily_rows dr
        ON dr.day_jst = ds.day_jst
      ORDER BY ds.day_jst
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const buildSeasonRangeQuery = (seasonIds) => {
  const seasonIdList = seasonIds.map((seasonId) => `'${seasonId}'`).join(", ");

  return {
    query: `
      SELECT
        DATE_FORMAT(MIN(CONVERT_TZ(gt.start_datetime, 'UTC', 'Asia/Tokyo')), '%Y-%m-%d') AS start_jst,
        DATE_FORMAT(MAX(CONVERT_TZ(gt.end_datetime, 'UTC', 'Asia/Tokyo')), '%Y-%m-%d') AS end_jst
      FROM game_space gs
      JOIN game_term gt
        ON gt.id = gs.game_term_id
      WHERE gs.id IN (${seasonIdList});
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const buildEventMetadataQuery = (seasonIds, startUtc, endUtc) => {
  const seasonIdList = seasonIds.map((seasonId) => `'${seasonId}'`).join(", ");

  return {
    query: `
      SELECT
        be.game_space_id,
        be.id AS boost_event_id,
        COALESCE(
          (
            SELECT NULLIF(TRIM(bei.event_name), '')
            FROM boost_event_info bei
            WHERE bei.boost_event_id = be.id
              AND bei.language_code = 'JA'
            LIMIT 1
          ),
          (
            SELECT NULLIF(TRIM(bel.label), '')
            FROM boost_event_label bel
            WHERE bel.boost_event_id = be.id
              AND bel.language_code = 'JA'
              AND bel.sort_order = 1
            LIMIT 1
          ),
          CAST(be.id AS CHAR)
        ) AS boost_event_name,
        be.start_datetime,
        be.end_datetime
      FROM boost_event be
      WHERE be.game_space_id IN (${seasonIdList})
        AND be.start_datetime < '${endUtc}'
        AND be.end_datetime >= '${startUtc}'
      ORDER BY be.start_datetime, be.id
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const buildEventRowsQuery = (seasonIds, startUtc, endUtc, selectedBoostEventId = ALL_BOOST_EVENTS_VALUE) => {
  const seasonIdList = seasonIds.map((seasonId) => `'${seasonId}'`).join(", ");
  const boostEventFilterClause = buildBoostEventFilterClause(selectedBoostEventId);

  return {
    query: `
      WITH selected_events AS (
        SELECT
          be.id AS boost_event_id,
          be.game_space_id,
          be.start_datetime,
          be.end_datetime,
          cb.id AS capital_budget_id,
          COALESCE(
            (
              SELECT NULLIF(TRIM(bei.event_name), '')
              FROM boost_event_info bei
              WHERE bei.boost_event_id = be.id
                AND bei.language_code = 'JA'
              LIMIT 1
            ),
            (
              SELECT NULLIF(TRIM(bel.label), '')
              FROM boost_event_label bel
              WHERE bel.boost_event_id = be.id
                AND bel.language_code = 'JA'
                AND bel.sort_order = 1
              LIMIT 1
            ),
            CAST(be.id AS CHAR)
          ) AS boost_event_name
        FROM boost_event be
        JOIN capital_budget cb
          ON cb.boost_event_id = be.id
        WHERE be.game_space_id IN (${seasonIdList})
          AND be.start_datetime < '${endUtc}'
          AND be.end_datetime >= '${startUtc}'
          ${boostEventFilterClause}
      ),
      target_assets AS (
        SELECT
          se.boost_event_id,
          COUNT(DISTINCT btal.power_asset_id) AS target_count
        FROM selected_events se
        JOIN boost_target_asset_list btal
          ON btal.boost_event_id = se.boost_event_id
        JOIN budget b
          ON b.capital_budget_id = se.capital_budget_id
         AND b.power_asset_id = btal.power_asset_id
        LEFT JOIN budget_event pre_budget_event
          ON pre_budget_event.budget_id = b.id
         AND pre_budget_event.occurred_datetime < se.start_datetime
        LEFT JOIN budget_event_of_reward_granted pre_reward
          ON pre_reward.id = pre_budget_event.id
        WHERE NOT EXISTS (
          SELECT 1
          FROM power_asset_event ppae
          WHERE ppae.game_space_id = se.game_space_id
            AND ppae.power_asset_id = btal.power_asset_id
            AND ppae.type = 'PHOTOGRAPH'
            AND ppae.occurred_datetime < se.start_datetime
        )
        AND pre_reward.id IS NULL
        GROUP BY se.boost_event_id
      ),
      approved_shot_rows AS (
        SELECT
          se.boost_event_id,
          COUNT(DISTINCT rr.power_asset_id) AS shot_count
        FROM selected_events se
        JOIN review_request_event rre
          ON rre.game_space_id = se.game_space_id
         AND rre.type = 'APPROVED'
        JOIN review_request_event_of_approved rreoa
          ON rreoa.id = rre.id
        JOIN review_request rr
          ON rr.id = rre.review_request_id
        JOIN boost_target_asset_list btal
          ON btal.boost_event_id = se.boost_event_id
         AND btal.power_asset_id = rr.power_asset_id
        WHERE rre.occurred_datetime >= '${startUtc}'
          AND rre.occurred_datetime < '${endUtc}'
        GROUP BY se.boost_event_id
  )
  SELECT
        se.game_space_id,
        se.boost_event_id,
        se.boost_event_name,
        se.start_datetime,
        se.end_datetime,
        COALESCE(ta.target_count, 0) AS target_count,
        COALESCE(asr.shot_count, 0) AS shot_count
      FROM selected_events se
      LEFT JOIN target_assets ta
        ON ta.boost_event_id = se.boost_event_id
      LEFT JOIN approved_shot_rows asr
        ON asr.boost_event_id = se.boost_event_id
      ORDER BY se.start_datetime, se.boost_event_id
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const buildEventApprovalTargetAssetsBase = (seasonIds, startUtc, endUtc, selectedBoostEventId = ALL_BOOST_EVENTS_VALUE) => {
  const seasonIdList = seasonIds.map((seasonId) => `'${seasonId}'`).join(", ");
  const boostEventFilterClause = buildBoostEventFilterClause(selectedBoostEventId);

  return `
    WITH selected_events AS (
      SELECT
        be.id AS boost_event_id,
        be.game_space_id,
        be.start_datetime,
        be.end_datetime
      FROM boost_event be
      WHERE be.game_space_id IN (${seasonIdList})
        AND be.start_datetime < '${endUtc}'
        AND be.end_datetime >= '${startUtc}'
        ${boostEventFilterClause}
    ),
    target_assets AS (
      SELECT DISTINCT
        btal.power_asset_id
      FROM selected_events se
      JOIN boost_target_asset_list btal
        ON btal.boost_event_id = se.boost_event_id
    )
  `;
};

const buildEventReviewOkCountQuery = (seasonIds, startUtc, endUtc, selectedBoostEventId) => ({
  query: `
    ${buildEventApprovalTargetAssetsBase(seasonIds, startUtc, endUtc, selectedBoostEventId)}
    SELECT
      COUNT(DISTINCT rr.power_asset_id) AS approved_review_request_count
    FROM review_request_event_of_approved rreoa
    JOIN review_request_event rre
      ON rre.id = rreoa.id
    JOIN review_request rr
      ON rr.id = rre.review_request_id
    JOIN target_assets ta
      ON ta.power_asset_id = rr.power_asset_id
    WHERE rr.game_space_id IN (${seasonIds.map((seasonId) => `'${seasonId}'`).join(", ")})
      AND rre.type = 'APPROVED'
      AND rre.occurred_datetime >= '${startUtc}'
      AND rre.occurred_datetime < '${endUtc}'
  `,
  database: "KpiDashboard",
  type: "list",
});

const buildEventUniqueShooterCountQuery = (seasonIds, startUtc, endUtc, selectedBoostEventId) => ({
  query: `
    ${buildEventApprovalTargetAssetsBase(seasonIds, startUtc, endUtc, selectedBoostEventId)}
    SELECT
      COUNT(DISTINCT rr.player_id) AS approved_unique_player_count
    FROM review_request_event_of_approved rreoa
    JOIN review_request_event rre
      ON rre.id = rreoa.id
    JOIN review_request rr
      ON rr.id = rre.review_request_id
    JOIN target_assets ta
      ON ta.power_asset_id = rr.power_asset_id
    WHERE rr.game_space_id IN (${seasonIds.map((seasonId) => `'${seasonId}'`).join(", ")})
      AND rre.type = 'APPROVED'
      AND rre.occurred_datetime >= '${startUtc}'
      AND rre.occurred_datetime < '${endUtc}'
  `,
  database: "KpiDashboard",
  type: "list",
});

const buildEventFirstShooterCountQuery = (seasonIds, startUtc, endUtc, selectedBoostEventId) => ({
  query: `
    ${buildEventApprovalTargetAssetsBase(seasonIds, startUtc, endUtc, selectedBoostEventId)}
    SELECT
      COUNT(*) AS first_approved_count
    FROM (
      SELECT
        rr.player_id,
        MIN(rre.occurred_datetime) AS first_approved_datetime
      FROM review_request_event_of_approved rreoa
      JOIN review_request_event rre
        ON rre.id = rreoa.id
      JOIN review_request rr
        ON rr.id = rre.review_request_id
      JOIN target_assets ta
        ON ta.power_asset_id = rr.power_asset_id
      WHERE rr.game_space_id IN (${seasonIds.map((seasonId) => `'${seasonId}'`).join(", ")})
        AND rre.type = 'APPROVED'
        AND rre.occurred_datetime >= '${startUtc}'
        AND rre.occurred_datetime < '${endUtc}'
      GROUP BY rr.player_id
    ) AS approved_players
    WHERE approved_players.first_approved_datetime >= '${startUtc}'
      AND approved_players.first_approved_datetime < '${endUtc}'
  `,
  database: "KpiDashboard",
  type: "list",
});

const parseCountRows = (rows, key) => Number(parseRows(rows)?.[0]?.[key] || 0);

const parseDateRange = (dateRange, fallbackStartJst, fallbackEndJst) => {
  if (Array.isArray(dateRange) && dateRange.length >= 2) {
    const [startDate, endDate] = dateRange;
    if (startDate && endDate) {
      return [startDate, endDate];
    }
  }

  return [fallbackStartJst, fallbackEndJst];
};

const capEndDateToTodayJst = (endJst) => {
  const todayJst = moment.tz("Asia/Tokyo").format("YYYY-MM-DD");
  if (!endJst) {
    return todayJst;
  }

  return moment(endJst).isAfter(moment(todayJst)) ? todayJst : endJst;
};

const parseRows = (rows) => {
  if (Array.isArray(rows)) {
    return rows;
  }

  const body = rows && Object.prototype.hasOwnProperty.call(rows, "body")
    ? rows.body
    : rows;

  if (Array.isArray(body)) {
    return body;
  }

  if (typeof body === "string") {
    try {
      const parsed = JSON.parse(body || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  return [];
};

const normalizeEventRows = (rows) => (
  Array.isArray(rows) ? rows : []
).map((row) => ({
  seasonId: row?.game_space_id || null,
  id: row?.boost_event_id || null,
  name: row?.boost_event_name || row?.boost_event_id || "ブーストイベント",
  startDatetime: row?.start_datetime || null,
  endDatetime: row?.end_datetime || null,
  period: row?.start_datetime && row?.end_datetime
    ? `${moment.tz(row.start_datetime, "UTC").tz("Asia/Tokyo").format("YYYY/MM/DD")} - ${moment.tz(row.end_datetime, "UTC").tz("Asia/Tokyo").format("YYYY/MM/DD")}`
    : "",
  targetCount: Number(row?.target_count || 0),
  shotCount: Number(row?.shot_count || 0),
  payoutAmount: Number(row?.payout_amount || 0),
}));

const aggregateTrendDays = (rows) => {
  const dayMap = new Map();

  (rows || []).forEach((row) => {
    const day = row.startDatetime ? moment.tz(row.startDatetime, "UTC").tz("Asia/Tokyo").format("YYYY-MM-DD") : "";
    if (!day) {
      return;
    }

    const current = dayMap.get(day) || {
      targetCount: 0,
      shotCount: 0,
      eventCount: 0,
    };

    current.targetCount += Number(row.targetCount) || 0;
    current.shotCount += Number(row.shotCount) || 0;
    current.eventCount += 1;
    dayMap.set(day, current);
  });

  const days = Array.from(dayMap.keys()).sort();

  return {
    date: days.map((day) => moment(day).format("M/D")),
    uniqueShots: days.map((day) => Number(dayMap.get(day)?.targetCount) || 0),
    reviewerOk: days.map((day) => Number(dayMap.get(day)?.shotCount) || 0),
    uniqueShooters: days.map((day) => Number(dayMap.get(day)?.eventCount) || 0),
  };
};

const aggregateSeasonChart = (rows, seasonOptions, selectedSeasonIds) => {
  const seasonIdOrder = sortSeasonIds(selectedSeasonIds);
  const seasonLabels = seasonIdOrder.map((seasonId) => seasonOptions.find((option) => option.value === seasonId)?.label || resolveSeasonLabel(seasonId));
  const seasonTotals = new Map();

  seasonIdOrder.forEach((seasonId) => {
    seasonTotals.set(seasonId, {
      targetCount: 0,
      shotCount: 0,
    });
  });

  (rows || []).forEach((row) => {
    const seasonId = row.seasonId;
    if (!seasonTotals.has(seasonId)) {
      return;
    }

    const current = seasonTotals.get(seasonId);
    current.targetCount += Number(row.targetCount) || 0;
    current.shotCount += Number(row.shotCount) || 0;
    seasonTotals.set(seasonId, current);
  });

  return {
    labels: seasonLabels,
    series: [
      {
        name: "対象電柱数",
        data: seasonIdOrder.map((seasonId) => Number(seasonTotals.get(seasonId)?.targetCount) || 0),
      },
      {
        name: "撮影数",
        data: seasonIdOrder.map((seasonId) => Number(seasonTotals.get(seasonId)?.shotCount) || 0),
      },
    ],
  };
};

const buildEventRowsFromQuery = (rows, selectedBoostEventId = ALL_BOOST_EVENTS_VALUE) => {
  const eventRows = normalizeEventRows(rows);
  const summarySourceRows = isAllBoostEvents(selectedBoostEventId)
    ? eventRows
    : eventRows.filter((row) => row.id === selectedBoostEventId);

  const summaryCounts = (Array.isArray(summarySourceRows) ? summarySourceRows : []).reduce((acc, row) => {
    acc.targetCount += Number(row.targetCount) || 0;
    acc.uniqueShotCount += Number(row.shotCount) || 0;
    return acc;
  }, {
    targetCount: 0,
    uniqueShotCount: 0,
  });

  return {
    eventRows,
    summaryCounts,
  };
};

const buildEventTrend = (rows) => {
  const dailyRows = Array.isArray(rows) ? rows : [];
  return {
    date: dailyRows.map((row) => {
      const day = row?.day_jst || "";
      return day ? moment.utc(day).tz("Asia/Tokyo").format("M/D") : "";
    }),
    uniqueShots: dailyRows.map((row) => Number(row?.unique_shot_count || 0)),
    reviewerOk: dailyRows.map((row) => Number(row?.review_ok_count || 0)),
    uniqueShooters: dailyRows.map((row) => Number(row?.unique_shooter_count || 0)),
  };
};

const buildEventKpis = (summary) => {
  const uniqueShotCount = Number(summary.uniqueShotCount || 0);
  const targetCount = Number(summary.targetCount || 0);
  const reviewOkCount = Number(summary.reviewOkCount || 0);
  const uniqueShooterCount = Number(summary.uniqueShooterCount || 0);
  const firstShooterCount = Number(summary.firstShooterCount || 0);
  const uniqueShotLoading = Boolean(summary.uniqueShotLoading);
  const achievementRateLoading = Boolean(summary.achievementRateLoading);
  const reviewOkLoading = Boolean(summary.reviewOkLoading);
  const uniqueShooterLoading = Boolean(summary.uniqueShooterLoading);
  const firstShooterLoading = Boolean(summary.firstShooterLoading);
  const achievementRate = targetCount > 0 ? (reviewOkCount / targetCount) * 100 : 0;
  const okRate = uniqueShotCount > 0 ? (reviewOkCount / uniqueShotCount) * 100 : 0;

  return [
    {
      key: "uniqueShot",
      title: "ユニーク撮影本数",
      value: uniqueShotLoading ? "--" : formatNumber(uniqueShotCount),
      subtitle: uniqueShotLoading ? "" : "ブースト対象",
      loading: uniqueShotLoading,
    },
    {
      key: "achievementRate",
      title: "撮影達成率",
      value: achievementRateLoading ? "--" : `${achievementRate.toFixed(1)}%`,
      subtitle: achievementRateLoading ? "対象 --本" : `対象 ${formatNumber(targetCount)}本`,
      loading: achievementRateLoading,
    },
    {
      key: "reviewOk",
      title: "レビューOK数",
      value: reviewOkLoading ? "--" : formatNumber(reviewOkCount),
      subtitle: reviewOkLoading ? "OK率 --%" : `OK率 ${okRate.toFixed(1)}%`,
      loading: reviewOkLoading,
    },
    {
      key: "uniqueShooter",
      title: "ユニーク撮影者数",
      value: uniqueShooterLoading ? "--" : formatNumber(uniqueShooterCount),
      subtitle: "累計参加者",
      loading: uniqueShooterLoading,
    },
    {
      key: "firstShooter",
      title: "初回撮影者数",
      value: firstShooterLoading ? "--" : formatNumber(firstShooterCount),
      subtitle: "今期新規",
      loading: firstShooterLoading,
    },
  ];
};

const createEmptyEventSummaryState = () => ({
  uniqueShotCount: 0,
  targetCount: 0,
  reviewOkCount: 0,
  uniqueShooterCount: 0,
  firstShooterCount: 0,
  uniqueShotLoading: true,
  achievementRateLoading: true,
  reviewOkLoading: true,
  uniqueShooterLoading: true,
  firstShooterLoading: true,
});

function* handleRegularSeasonEventBootstrapData(action) {
  try {
    yield put(setPicTreeRegularSeasonEventPageData({
      bootstrapEventLoading: true,
      bootstrapEventReady: false,
      eventRowsLoading: true,
      eventRows: [],
      selectedBoostEventId: ALL_BOOST_EVENTS_VALUE,
      selectedDateRange: [null, null],
    }));

    const selectedSeasonId = action?.payload?.selectedSeasonId || "cmunxinty000079zc6zf595vs";
    const selectedSeasonIds = normalizeSelectionSeasonIds(action?.payload?.selectedSeasonIds, selectedSeasonId);
    const jwtToken = yield call(getJwtToken);
    const seasonIdList = sortSeasonIds(selectedSeasonIds);

    if (!seasonIdList.length) {
      yield put(setPicTreeRegularSeasonEventPageData({
        bootstrapEventLoading: false,
        bootstrapEventReady: false,
        eventRowsLoading: false,
        eventRows: [],
      }));
      return;
    }

    const seasonRangeRows = yield call(
      callWithTimeoutAndRetry,
      getDataFromSxi,
      [buildSeasonRangeQuery(seasonIdList), ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    );

    const seasonRange = parseRows(seasonRangeRows)?.[0] || {};
    const [startJstRaw, endJstRaw] = parseDateRange(null, seasonRange?.start_jst, seasonRange?.end_jst);
    const startJst = startJstRaw;
    const endJst = capEndDateToTodayJst(endJstRaw);

    if (!startJst || !endJst) {
      yield put(setPicTreeRegularSeasonEventPageData({
        bootstrapEventLoading: false,
        bootstrapEventReady: false,
        eventRowsLoading: false,
        eventRows: [],
        selectedBoostEventId: ALL_BOOST_EVENTS_VALUE,
        selectedDateRange: [startJst || null, endJst || null],
      }));
      return;
    }

    const startUtc = moment.tz(startJst, "Asia/Tokyo").startOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
    const endUtc = moment.tz(endJst, "Asia/Tokyo").add(1, "day").startOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
    const eventRowsQuery = buildEventMetadataQuery(seasonIdList, startUtc, endUtc);

    const eventRowsResult = yield call(
      callWithTimeoutAndRetry,
      getDataFromSxi,
      [eventRowsQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    );

    const { eventRows } = buildEventRowsFromQuery(parseRows(eventRowsResult), ALL_BOOST_EVENTS_VALUE);
    const firstEventRow = eventRows.find((row) => row && row.id) || null;
    const firstEventId = firstEventRow?.id || ALL_BOOST_EVENTS_VALUE;
    const firstStartJst = firstEventRow?.startDatetime
      ? moment.utc(firstEventRow.startDatetime).tz("Asia/Tokyo").format("YYYY-MM-DD")
      : startJst;
    const firstEndJst = firstEventRow?.endDatetime
      ? moment.min(
        moment.utc(firstEventRow.endDatetime).tz("Asia/Tokyo").startOf("day"),
        moment().tz("Asia/Tokyo").startOf("day")
      ).format("YYYY-MM-DD")
      : endJst;

    yield put(setPicTreeRegularSeasonEventPageData({
      selectedSeasonId,
      selectedSeasonIds: seasonIdList,
      selectedBoostEventId: firstEventId,
      selectedDateRange: [firstStartJst, firstEndJst],
      bootstrapEventLoading: false,
      bootstrapEventReady: true,
      eventRows,
      eventRowsLoading: false,
    }));
  } catch (error) {
    yield put(setPicTreeRegularSeasonEventPageData({
      bootstrapEventLoading: false,
      bootstrapEventReady: false,
      eventRowsLoading: false,
      eventRows: [],
      selectedBoostEventId: ALL_BOOST_EVENTS_VALUE,
    }));
  }
}

function* handleRegularSeasonEventPageData(action) {
  try {
    yield put(setPicTreeRegularSeasonEventPageLoading(true));
    yield put(setPageTopLoader(true));

    const selectedSeasonId = action?.payload?.selectedSeasonId || "cmunxinty000079zc6zf595vs";
    const selectedSeasonIds = normalizeSelectionSeasonIds(action?.payload?.selectedSeasonIds, selectedSeasonId);
    const selectedBoostEventId = action?.payload?.selectedBoostEventId || ALL_BOOST_EVENTS_VALUE;
    const selectedDateRange = action?.payload?.selectedDateRange;
    const initialEventRows = Array.isArray(action?.payload?.eventRows) ? action.payload.eventRows : [];

    if (isAllBoostEvents(selectedBoostEventId)) {
      yield put(setPageTopLoader(false));
      yield put(setPicTreeRegularSeasonEventPageLoading(false));
      return;
    }

    yield put(setPicTreeRegularSeasonEventPageData({
      ...emptyPageData(selectedSeasonId, selectedSeasonIds),
      selectedBoostEventId,
      eventRows: initialEventRows,
      selectedDateRange: Array.isArray(selectedDateRange) && selectedDateRange.length >= 2
        ? selectedDateRange
        : [null, null],
    }));

    const jwtToken = yield call(getJwtToken);
    const seasonIdList = sortSeasonIds(selectedSeasonIds);

    if (!seasonIdList.length) {
      yield put(setPicTreeRegularSeasonEventPageData({
        kpis: readyEmptyKpis(),
        trend: emptyTrend(),
        trendLoading: false,
        trendRows: [],
        trendRowsLoading: false,
        seasonChart: emptySeasonChart(),
        seasonChartLoading: false,
        eventRows: [],
        eventRowsLoading: false,
      }));
      return;
    }

    const seasonRangeRows = yield call(
      callWithTimeoutAndRetry,
      getDataFromSxi,
      [buildSeasonRangeQuery(seasonIdList), ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    );

    const seasonRange = parseRows(seasonRangeRows)?.[0] || {};
    const [startJstRaw, endJstRaw] = parseDateRange(selectedDateRange, seasonRange?.start_jst, seasonRange?.end_jst);
    const startJst = startJstRaw;
    const endJst = capEndDateToTodayJst(endJstRaw);

    if (!startJst || !endJst) {
      yield put(setPicTreeRegularSeasonEventPageData({
        kpis: readyEmptyKpis(),
        trend: emptyTrend(),
        trendRows: [],
        eventRowsLoading: false,
        trendLoading: false,
        seasonChart: emptySeasonChart(),
        seasonChartLoading: false,
        eventRows: [],
        selectedBoostEventId,
        selectedDateRange: [startJst || null, endJst || null],
      }));
      return;
    }

    const effectiveSelectedBoostEventId = selectedBoostEventId;
    const effectiveStartJst = startJst;
    const effectiveEndJst = endJst;
    const startUtc = moment.tz(effectiveStartJst, "Asia/Tokyo").startOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
    const endUtc = moment.tz(effectiveEndJst, "Asia/Tokyo").add(1, "day").startOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
    const eventRowsQuery = buildEventRowsQuery(
      seasonIdList,
      startUtc,
      endUtc,
      effectiveSelectedBoostEventId
    );
    const eventDailyTrendQuery = buildEventDailyTrendQuery(seasonIdList, startUtc, endUtc, effectiveStartJst, effectiveEndJst, effectiveSelectedBoostEventId);
    const eventReviewOkCountQuery = buildEventReviewOkCountQuery(seasonIdList, startUtc, endUtc, effectiveSelectedBoostEventId);
    const eventUniqueShooterCountQuery = buildEventUniqueShooterCountQuery(seasonIdList, startUtc, endUtc, effectiveSelectedBoostEventId);
    const eventFirstShooterCountQuery = buildEventFirstShooterCountQuery(seasonIdList, startUtc, endUtc, effectiveSelectedBoostEventId);

    const summaryState = createEmptyEventSummaryState();
    let latestTrendRows = [];
    let trendLoading = true;
    const syncAchievementLoading = () => {
      summaryState.achievementRateLoading = summaryState.uniqueShotLoading || summaryState.reviewOkLoading;
    };
    const publishEventPageData = function* (overrides = {}) {
      yield put(setPicTreeRegularSeasonEventPageData({
        kpis: buildEventKpis(summaryState),
        trend: buildEventTrend(latestTrendRows),
        trendLoading,
        trendRows: latestTrendRows,
        trendRowsLoading: trendLoading,
        selectedBoostEventId: effectiveSelectedBoostEventId,
        selectedDateRange: [effectiveStartJst, effectiveEndJst],
        ...overrides,
      }));
    };

    const fetchEventRowsSection = function* () {
      try {
        const eventRowsResult = yield call(
          callWithTimeoutAndRetry,
          getDataFromSxi,
          [eventRowsQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
          SXI_HEAVY_RETRY
        );

        const { eventRows: metricRows, summaryCounts } = buildEventRowsFromQuery(
          parseRows(eventRowsResult),
          effectiveSelectedBoostEventId
        );
        const metricRowsById = new Map(metricRows.map((row) => [row.id, row]));
        const eventRows = initialEventRows.length > 0
          ? initialEventRows.map((row) => ({
            ...row,
            ...(metricRowsById.get(row.id) || {}),
          }))
          : metricRows;

        summaryState.uniqueShotCount = Number(summaryCounts.uniqueShotCount || 0);
        summaryState.targetCount = Number(summaryCounts.targetCount || 0);
        summaryState.uniqueShotLoading = false;
        syncAchievementLoading();

        yield publishEventPageData({
          eventRows,
          eventRowsLoading: false,
        });
      } catch (error) {
        summaryState.uniqueShotCount = 0;
        summaryState.targetCount = 0;
        summaryState.uniqueShotLoading = false;
        syncAchievementLoading();

        yield publishEventPageData({
          eventRows: [],
          eventRowsLoading: false,
        });
      }
    };

    const fetchEventTrendSection = function* () {
      try {
        const eventDailyTrendResult = yield call(
          callWithTimeoutAndRetry,
          getDataFromSxi,
          [eventDailyTrendQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
          SXI_HEAVY_RETRY
        );

        latestTrendRows = parseRows(eventDailyTrendResult);
        trendLoading = false;

        yield publishEventPageData({
          trend: buildEventTrend(latestTrendRows),
          trendLoading: false,
          trendRows: latestTrendRows,
          trendRowsLoading: false,
        });
      } catch (error) {
        latestTrendRows = [];
        trendLoading = false;

        yield publishEventPageData({
          trend: emptyTrend(),
          trendLoading: false,
          trendRows: [],
          trendRowsLoading: false,
        });
      }
    };

    const fetchEventReviewOkSection = function* () {
      try {
        const eventReviewOkCountResult = yield call(
          callWithTimeoutAndRetry,
          getDataFromSxi,
          [eventReviewOkCountQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
          SXI_HEAVY_RETRY
        );

        summaryState.reviewOkCount = parseCountRows(eventReviewOkCountResult, "approved_review_request_count");
        summaryState.reviewOkLoading = false;
        syncAchievementLoading();

        yield publishEventPageData({
        });
      } catch (error) {
        summaryState.reviewOkCount = 0;
        summaryState.reviewOkLoading = false;
        syncAchievementLoading();

        yield publishEventPageData({
        });
      }
    };

    const fetchEventUniqueShooterSection = function* () {
      try {
        const eventUniqueShooterCountResult = yield call(
          callWithTimeoutAndRetry,
          getDataFromSxi,
          [eventUniqueShooterCountQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
          SXI_HEAVY_RETRY
        );

        summaryState.uniqueShooterCount = parseCountRows(eventUniqueShooterCountResult, "approved_unique_player_count");
        summaryState.uniqueShooterLoading = false;
        yield publishEventPageData({
        });
      } catch (error) {
        summaryState.uniqueShooterCount = 0;
        summaryState.uniqueShooterLoading = false;
        yield publishEventPageData({
        });
      }
    };

    const fetchEventFirstShooterSection = function* () {
      try {
        const eventFirstShooterCountResult = yield call(
          callWithTimeoutAndRetry,
          getDataFromSxi,
          [eventFirstShooterCountQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
          SXI_HEAVY_RETRY
        );

        summaryState.firstShooterCount = parseCountRows(eventFirstShooterCountResult, "first_approved_count");
        summaryState.firstShooterLoading = false;
        yield publishEventPageData({
        });
      } catch (error) {
        summaryState.firstShooterCount = 0;
        summaryState.firstShooterLoading = false;
        yield publishEventPageData({
        });
      }
    };

    yield call(fetchEventRowsSection);
    yield call(fetchEventTrendSection);
    yield call(fetchEventReviewOkSection);
    yield call(fetchEventUniqueShooterSection);
    yield call(fetchEventFirstShooterSection);
  } catch (error) {
    console.log(error);
    yield put(setPicTreeRegularSeasonEventPageData({
      kpis: readyEmptyKpis(),
      trend: emptyTrend(),
      trendLoading: false,
      trendRows: [],
      trendRowsLoading: false,
      eventRows: [],
      eventRowsLoading: false,
    }));
  } finally {
    const wasCancelled = yield cancelled();
    if (!wasCancelled) {
      yield put(setPageTopLoader(false));
      yield put(setPicTreeRegularSeasonEventPageLoading(false));
    }
  }
}

export default function* pictreeRegularSeasonEventSaga() {
  yield takeEvery(PICTREE.GET_REGULAR_SEASON_EVENT_BOOTSTRAP_DATA, handleRegularSeasonEventBootstrapData);
  yield takeLatest(PICTREE.GET_REGULAR_SEASON_EVENT_PAGE_DATA, handleRegularSeasonEventPageData);
}
